import { FC, ReactElement } from "react";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

interface Props {
	/**
	 * True if the user is not allowed to use the wrapped element.
	 */
	forbidden: boolean;

	/**
	 * The optional tooltip text; defaults to the "page forbidden" message.
	 */
	title?: string;

	/**
	 * The wrapped element, usually a disabled button.
	 */
	children: ReactElement;
}

/**
 * ForbiddenTooltip explains, on hover, why an element is disabled for lack of permissions.
 * Disabled elements do not fire mouse events, so they are wrapped in a span.
 *
 * @param forbidden true if the user is not allowed to use the element
 * @param title     the optional tooltip text
 * @param children  the wrapped element
 * @returns         the element, with the tooltip if forbidden
 */
const ForbiddenTooltip: FC<Props> = ({ forbidden, title, children }) => {
	const { t } = useTranslation("common");

	if (!forbidden) {
		return children;
	}

	return (
		<Tooltip arrow title={title ?? t("content locked/page forbidden")}>
			<span>{children}</span>
		</Tooltip>
	);
};

export default ForbiddenTooltip;
